<script>
  import { onMount } from "svelte";
  import { user } from "../../stores/auth.js";

  const API_URL = window.RUNTIME_CONFIG?.API_URL || 'http://localhost:8004';

  let activities = [];
  let loading = true;
  let error = null;

  // Filter state
  let selectedFilter = "all"; // all, trades, dividends, transfers
  let pageSize = 50;

  // PDT Status
  let pdtStatus = null;
  let pdtLoading = true;

  const filterOptions = [
    { value: "all", label: "All Activities" },
    { value: "trades", label: "Trade Fills" },
    { value: "dividends", label: "Dividends" },
    { value: "transfers", label: "Transfers" },
  ];

  onMount(async () => {
    await Promise.all([
      loadActivities(),
      loadPdtStatus(),
    ]);
  });

  async function loadPdtStatus() {
    pdtLoading = true;
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${API_URL}/api/account/pdt-check`, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (response.ok) {
        pdtStatus = await response.json();
      }
    } catch (err) {
      console.error("Failed to load PDT status:", err);
    } finally {
      pdtLoading = false;
    }
  }

  async function loadActivities() {
    loading = true;
    error = null;

    try {
      const token = localStorage.getItem("access_token");

      // Build URL based on filter
      let url = `${API_URL}/api/activities/`;
      const params = new URLSearchParams();
      params.append("page_size", pageSize.toString());

      if (selectedFilter === "trades") {
        url = `${API_URL}/api/activities/trade-history`;
      } else if (selectedFilter === "dividends") {
        url = `${API_URL}/api/activities/dividends`;
      } else if (selectedFilter === "transfers") {
        url = `${API_URL}/api/activities/transfers`;
      }

      const response = await fetch(`${url}?${params}`, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to load activities");
      }

      const data = await response.json();
      // Handle different response keys based on endpoint
      activities = data.activities || data.trades || data.dividends || data.transfers || [];

    } catch (err) {
      error = err.message;
      activities = [];
    } finally {
      loading = false;
    }
  }

  function handleFilterChange() {
    loadActivities();
  }

  function getActivityIcon(type) {
    switch(type) {
      case "FILL":
        return "fas fa-exchange-alt";
      case "DIV":
      case "DIVCGL":
      case "DIVCGS":
      case "DIVNRA":
      case "DIVROC":
      case "DIVTXEX":
        return "fas fa-coins";
      case "TRANS":
      case "CSD":
      case "CSW":
        return "fas fa-university";
      case "JNLC":
      case "JNLS":
        return "fas fa-book";
      case "INT":
        return "fas fa-percentage";
      case "FEE":
        return "fas fa-receipt";
      default:
        return "fas fa-history";
    }
  }

  function getActivityColor(type) {
    switch(type) {
      case "FILL":
        return "bg-blue-50 text-blue-600";
      case "DIV":
      case "DIVCGL":
      case "DIVCGS":
      case "DIVNRA":
      case "DIVROC":
      case "DIVTXEX":
        return "bg-emerald-50 text-emerald-600";
      case "TRANS":
      case "CSD":
      case "CSW":
        return "bg-amber-50 text-amber-600";
      case "JNLC":
      case "JNLS":
        return "bg-violet-50 text-violet-600";
      case "INT":
        return "bg-cyan-50 text-cyan-600";
      case "FEE":
        return "bg-red-50 text-red-600";
      default:
        return "bg-surface-variant text-ink-secondary";
    }
  }

  function getActivityLabel(type) {
    const labels = {
      "FILL": "Trade Fill",
      "TRANS": "Transfer",
      "DIV": "Dividend",
      "DIVCGL": "Capital Gain (Long)",
      "DIVCGS": "Capital Gain (Short)",
      "DIVNRA": "Dividend (NRA)",
      "DIVROC": "Return of Capital",
      "DIVTXEX": "Tax Exempt Dividend",
      "INT": "Interest",
      "JNLC": "Journal Cash",
      "JNLS": "Journal Securities",
      "FEE": "Fee",
      "CSD": "Cash Deposit",
      "CSW": "Cash Withdrawal",
    };
    return labels[type] || type;
  }

  function formatDate(dateStr) {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function formatCurrency(amount) {
    if (!amount) return "$0.00";
    const num = parseFloat(amount);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num);
  }
</script>

<header class="mb-6">
  <h1 class="text-2xl font-bold text-ink">Activities</h1>
  <p class="text-sm text-ink-secondary mt-1">Your trading activity, dividends, and transfers.</p>
</header>

<div class="space-y-6">

  <!-- PDT Status Card -->
  {#if !pdtLoading && pdtStatus}
    <div class="card card-pad">
      <h3 class="text-lg font-semibold text-ink mb-4">
        Pattern Day Trader Status
      </h3>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div class="rounded-xl bg-surface-variant p-4">
          <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Status</p>
          <p class="text-lg font-bold mt-1 {pdtStatus.is_pattern_day_trader ? 'text-red-600' : 'text-emerald-600'}">
            {pdtStatus.is_pattern_day_trader ? 'PDT Flagged' : 'Not PDT'}
          </p>
        </div>
        <div class="rounded-xl bg-surface-variant p-4">
          <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Day Trades (5 days)</p>
          <p class="text-lg font-bold text-ink tnum mt-1">{pdtStatus.day_trade_count} / 4</p>
        </div>
        <div class="rounded-xl bg-surface-variant p-4">
          <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">Account Equity</p>
          <p class="text-lg font-bold text-ink tnum mt-1">{formatCurrency(pdtStatus.equity)}</p>
        </div>
        <div class="rounded-xl bg-surface-variant p-4">
          <p class="text-xs font-semibold uppercase tracking-wider text-ink-tertiary">PDT Threshold</p>
          <p class="text-lg font-bold tnum mt-1 {pdtStatus.equity >= 25000 ? 'text-emerald-600' : 'text-amber-600'}">
            $25,000
          </p>
        </div>
      </div>

      {#if pdtStatus.would_trigger_pdt}
        <div class="banner banner-warning mt-4">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <strong>Warning:</strong> Your next day trade will trigger Pattern Day Trader status.
          PDT accounts with less than $25,000 equity face trading restrictions.
        </div>
      {:else if pdtStatus.trades_until_pdt !== null && pdtStatus.trades_until_pdt > 0}
        <div class="banner banner-info mt-4">
          <i class="fas fa-info-circle mr-2"></i>
          You can make <strong>{pdtStatus.trades_until_pdt}</strong> more day trade(s) before triggering PDT status.
        </div>
      {:else if pdtStatus.equity >= 25000}
        <div class="banner banner-success mt-4">
          <i class="fas fa-check-circle mr-2"></i>
          Your account equity exceeds $25,000, so PDT rules do not apply to you.
        </div>
      {/if}
    </div>
  {/if}

  <!-- Activities Card -->
  <div class="card overflow-hidden">
    <div class="px-6 pt-6 pb-4 flex flex-wrap items-center justify-between gap-3">
      <h3 class="text-lg font-semibold text-ink">
        Account Activities
      </h3>
      <select
        bind:value={selectedFilter}
        on:change={handleFilterChange}
        class="select !w-auto !py-2"
      >
        {#each filterOptions as option}
          <option value={option.value}>{option.label}</option>
        {/each}
      </select>
    </div>

    {#if loading}
      <div class="px-6 pb-6 space-y-3">
        {#each Array(6) as _}
          <div class="flex items-center gap-3">
            <div class="skeleton h-8 w-8 rounded-full"></div>
            <div class="skeleton h-4 flex-1"></div>
            <div class="skeleton h-4 w-24"></div>
          </div>
        {/each}
      </div>
    {:else if error}
      <div class="px-6 pb-10 pt-4 text-center">
        <i class="fas fa-exclamation-circle text-2xl text-ink-tertiary mb-3"></i>
        <p class="text-sm text-red-600">{error}</p>
        <button
          on:click={loadActivities}
          class="btn btn-outline btn-sm mt-4"
        >
          Try Again
        </button>
      </div>
    {:else if activities.length === 0}
      <div class="px-6 pb-10 pt-4 text-center">
        <i class="fas fa-inbox text-2xl text-ink-tertiary mb-3"></i>
        <p class="text-sm text-ink-secondary">
          {selectedFilter === "all" ? "No activities found. Start trading to see your activity here." : "No " + filterOptions.find(f => f.value === selectedFilter)?.label.toLowerCase() + " found"}
        </p>
      </div>
    {:else}
      <div class="overflow-x-auto">
        <table class="w-full border-collapse">
          <thead>
            <tr>
              <th class="th">Type</th>
              <th class="th">Details</th>
              <th class="th text-right">Amount</th>
              <th class="th text-right">Date</th>
            </tr>
          </thead>
          <tbody>
            {#each activities as activity}
              <tr class="hover:bg-canvas">
                <td class="td">
                  <div class="flex items-center">
                    <span class="w-8 h-8 rounded-full {getActivityColor(activity.activity_type)} flex items-center justify-center mr-3">
                      <i class="{getActivityIcon(activity.activity_type)} text-xs"></i>
                    </span>
                    <span class="text-sm font-semibold text-ink">
                      {getActivityLabel(activity.activity_type)}
                    </span>
                  </div>
                </td>
                <td class="td">
                  {#if activity.activity_type === "FILL"}
                    <span class="font-medium {activity.side === 'buy' ? 'text-emerald-600' : 'text-red-600'}">
                      {activity.side?.toUpperCase()}
                    </span>
                    <span class="text-sm font-semibold text-ink ml-2">{activity.symbol}</span>
                    <span class="text-xs text-ink-tertiary ml-2 tnum">
                      {activity.qty} @ {formatCurrency(activity.price)}
                    </span>
                  {:else if activity.symbol}
                    <span class="text-sm font-semibold text-ink">{activity.symbol}</span>
                    {#if activity.description}
                      <span class="text-xs text-ink-tertiary ml-2">{activity.description}</span>
                    {/if}
                  {:else if activity.description}
                    <span class="text-ink-secondary">{activity.description}</span>
                  {:else}
                    <span class="text-ink-tertiary">-</span>
                  {/if}
                </td>
                <td class="td text-right tnum">
                  {#if activity.activity_type === "FILL"}
                    {formatCurrency(parseFloat(activity.price) * parseFloat(activity.qty))}
                  {:else if activity.net_amount}
                    <span class="{parseFloat(activity.net_amount) >= 0 ? 'text-emerald-600' : 'text-red-600'} font-medium">
                      {formatCurrency(activity.net_amount)}
                    </span>
                  {:else}
                    <span class="text-ink-tertiary">-</span>
                  {/if}
                </td>
                <td class="td text-right text-ink-secondary tnum">
                  {formatDate(activity.transaction_time || activity.date)}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </div>

  <!-- Activity Type Legend -->
  <div class="card card-pad">
    <h3 class="text-lg font-semibold text-ink mb-4">
      Activity Types
    </h3>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div class="flex items-center">
        <span class="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mr-3 shrink-0">
          <i class="fas fa-exchange-alt text-xs"></i>
        </span>
        <div>
          <span class="text-sm font-medium text-ink">Trade Fill</span>
          <p class="text-xs text-ink-tertiary">Executed buy/sell orders</p>
        </div>
      </div>
      <div class="flex items-center">
        <span class="w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mr-3 shrink-0">
          <i class="fas fa-coins text-xs"></i>
        </span>
        <div>
          <span class="text-sm font-medium text-ink">Dividend</span>
          <p class="text-xs text-ink-tertiary">Dividend payments received</p>
        </div>
      </div>
      <div class="flex items-center">
        <span class="w-8 h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mr-3 shrink-0">
          <i class="fas fa-university text-xs"></i>
        </span>
        <div>
          <span class="text-sm font-medium text-ink">Transfer</span>
          <p class="text-xs text-ink-tertiary">Deposits & withdrawals</p>
        </div>
      </div>
      <div class="flex items-center">
        <span class="w-8 h-8 rounded-full bg-cyan-50 text-cyan-600 flex items-center justify-center mr-3 shrink-0">
          <i class="fas fa-percentage text-xs"></i>
        </span>
        <div>
          <span class="text-sm font-medium text-ink">Interest</span>
          <p class="text-xs text-ink-tertiary">Interest earned on cash</p>
        </div>
      </div>
      <div class="flex items-center">
        <span class="w-8 h-8 rounded-full bg-violet-50 text-violet-600 flex items-center justify-center mr-3 shrink-0">
          <i class="fas fa-book text-xs"></i>
        </span>
        <div>
          <span class="text-sm font-medium text-ink">Journal</span>
          <p class="text-xs text-ink-tertiary">Internal cash/securities transfers</p>
        </div>
      </div>
      <div class="flex items-center">
        <span class="w-8 h-8 rounded-full bg-red-50 text-red-600 flex items-center justify-center mr-3 shrink-0">
          <i class="fas fa-receipt text-xs"></i>
        </span>
        <div>
          <span class="text-sm font-medium text-ink">Fee</span>
          <p class="text-xs text-ink-tertiary">Account fees charged</p>
        </div>
      </div>
    </div>
  </div>

</div>
